import axios from 'axios';

// Get base URL from current domain
const getBaseUrl = () => {
    // Use localhost for API in development (Docker or local)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `${window.location.origin}/api/v1`;
    }
    const origin = window.location.origin;
    return `${origin}/api/v1`;
};

// Log base URL for debugging
const baseURL = process.env.VUE_APP_API_URL || getBaseUrl();

// Determine if we should use credentials based on environment
const shouldUseCredentials = () => {
    // Only use credentials for same-origin requests or specific domains
    if (window.location.hostname === 'localhost') {
        return false; // Don't use credentials for cross-origin requests from localhost
    }
    return window.location.hostname === 'hos.foxsol.net' || window.location.hostname === 'foxsol.net';
};

// Create axios instance with appropriate configuration
const api = axios.create({
    baseURL: baseURL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    // Longer timeout when backend is Docker + remote DB (cold start / slow queries)
    timeout: 90000,
    // Only use credentials for same-origin requests
    withCredentials: shouldUseCredentials()
});

// Add request interceptor
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('srToken');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            config.headers['X-Auth-Token'] = token;

            if (!config.data) {
                config.data = {};
            }

            // Always add token to request body if it's an object (override if exists to ensure it's from localStorage)
            if (typeof config.data === 'object' && !(config.data instanceof FormData)) {
                config.data.token = token;
            }
        }

        // Browser must set multipart boundary — never force Content-Type for FormData
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            if (config.headers && typeof config.headers.delete === 'function') {
                config.headers.delete('Content-Type');
            } else if (config.headers) {
                delete config.headers['Content-Type'];
                delete config.headers['content-type'];
            }
        }

        return config;
    },
    error => Promise.reject(error)
);

// Add interceptor for error handling
api.interceptors.response.use(
    response => {
        // Log successful response
    
        return response;
    },
    error => {
        // Check error type
        let errorMessage = 'Unknown error occurred';
        
        if (error.response) {
            // Server returned response with status other than 2xx
            // API envelope: { success, data, error: { code, message } }
            const data = error.response.data;
            errorMessage =
                data?.error?.message ||
                data?.message ||
                (typeof data?.error === 'string' ? data.error : null) ||
                `Server error: ${error.response.status}`;
            
            // Handle 401 Unauthorized — soft logout (avoid hard reload that remounts the whole app)
            if (error.response.status === 401) {
                if (!api.__handling401) {
                    api.__handling401 = true;
                    Promise.all([
                        import('@/store'),
                        import('@/router')
                    ])
                        .then(([{ default: store }, { default: router }]) => {
                            if (store.state.users?.user?.isLogged) {
                                store.commit('users/logoutUser');
                            } else {
                                try {
                                    localStorage.removeItem('srToken');
                                    localStorage.removeItem('srIsLogged');
                                } catch (e) { /* ignore */ }
                            }
                            const routeName = router.currentRoute.value?.name;
                            if (routeName && routeName !== 'login') {
                                router.replace({ name: 'login' });
                            }
                        })
                        .finally(() => {
                            api.__handling401 = false;
                        });
                }
            }
            
        } else if (error.code === 'ECONNABORTED') {
            errorMessage = 'Request timed out. The server is taking too long to respond.';
            
        } else if (error.request) {
            // Request was made but no response was received
            errorMessage = 'No response from server. Please check your connection';
            
        } else {
            // Something happened during request setup
            errorMessage = error.message;
            
        }
                
        return Promise.reject({
            error,
            message: errorMessage,
            config: error.config,
            response: error.response
        });
    }
);

// Add function to reset custom headers
const resetHeaders = () => {
    api.defaults.headers.common = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
    
    // Restore token from localStorage if it exists
    const token = localStorage.getItem('srToken');
    if (token) {
        api.defaults.headers.Authorization = `Bearer ${token}`;
    }
};

// Add CORS test function
const testCORS = async () => {
    try {
        console.log('Testing CORS configuration...');
        const response = await api.get('/cors-test');
        console.log('CORS test successful:', response.data);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('CORS test failed:', error);
        return { success: false, error: error.message };
    }
};

export { resetHeaders, testCORS };
export default api;
